/**
 * Nagrania głosu dziecka do strojenia oceny wymowy (SPEC §23.39).
 *
 * Po co: żeby później porównać ocenę Azure z prawdziwym głosem (i ewentualnie z drugim silnikiem), przy każdej UDANEJ próbie „powiedz zdanie” w krokach 1–2 lekcji
 * wysyłamy na serwer plik z głosem razem z zapisem oceny (ten sam, co w dzienniku prób). Dziecko niczego nie opisuje — dobre/złe rozstrzyga Azure.
 *
 * Zasady:
 * - tylko gdy rodzic włączył zgodę dla AKTYWNEGO profilu (`recordVoice`, domyślnie wyłączona); serwer sprawdza to jeszcze raz (403 'recording-off');
 * - tylko fazy `repeat` i `read` oraz tylko oceny Azure (samoocena i przeglądarka nie mają co porównywać);
 * - wysyłka w tle, po jednej; kolejka jest w pamięci (ocena Azure wymaga internetu, więc zwykle idzie od razu), ograniczona do kilku pozycji;
 * - błąd wysyłki nigdy nie psuje nauki: stałe odmowy serwera (limit, zły plik, brak miejsca na nagrania) kasują pozycję, chwilowe błędy ponawiamy (`MAX_TRIES` prób
 *   w sumie) co `RETRY_MS` — jedno zaplanowane ponowienie naraz, więc nowe próby dziecka nie przyspieszają go i nie zużywają jego budżetu; przerwę odmierza `setTimeout`
 *   (zegar monotoniczny), nie zegar ścienny, więc cofnięcie czasu w urządzeniu niczego nie zawiesza; zawieszone żądanie przerywa limit czasu;
 * - zgoda cofnięta na serwerze (403) albo profil zarchiwizowany (404): cała kolejka tego profilu jest kasowana, `onRevoked` mówi aplikacji, że trzeba odświeżyć profil
 *   (aplikacja robi to dopiero na ekranie głównym — nigdy w trakcie lekcji), a do czasu wczytania profilu na nowo kolejka nic z tego profilu nie przyjmuje
 *   (stary obiekt profilu nadal mówi `recordVoice: true`, więc bez tego każda kolejna próba leciałaby na serwer po następną odmowę);
 * - nagranie dostaje profil, przy którym dziecko ZACZĘŁO mówić (`profileId`): gdyby w trakcie oceny przełączono profil, głos nie trafi pod cudze imię;
 * - razem z nagraniem jedzie „oryginał z Azure” (SPEC §23.40): surowe JSON-y obu rozpoznawań (ocena wymowy i zwykłe rozpoznawanie) oraz kontekst (wersja kodu, progi, ustawienia
 *   Azure). Gdy łącznie przekraczają limit serwera, najpierw odpada zwykłe rozpoznawanie, potem ocena (pole `omitted`) — nagranie i tak jedzie.
 */
import type { AttemptRecord } from './attemptLog.ts';
import type { AzureRawPair } from '../speech/types.ts';
import { PCM_RATE } from './pcm.ts';
import { recordingContext } from './recordingContext.ts';
import type { RecordingContext } from './recordingContext.ts';
import { wavFromPcm16 } from './wav.ts';

export type RecordedPhase = 'repeat' | 'read';
/** Fazy zadania „powiedz zdanie”, które nagrywamy: krok 1 (powtarzanie) i krok 2 (czytanie). Scenka i budowanie zdań — nie. */
export const isRecordedPhase = (phase: string): phase is RecordedPhase => phase === 'repeat' || phase === 'read';

/** Ile nagrań czeka na wysłanie naraz (najstarsze wypada) — jedno to ok. 100–250 KB. */
export const MAX_QUEUE = 8;
/** Krótsze niż to nie jest mową; dłuższe niż limit serwera (1 MB ≈ 31 s) odrzuciłby serwer, więc nawet nie próbujemy. */
export const MIN_SECONDS = 0.2;
export const MAX_SECONDS = 28;
/** Ile razy próbujemy wysłać jedno nagranie po chwilowym błędzie i co ile ms. */
export const MAX_TRIES = 4;
export const RETRY_MS = 30_000;
/** Jedna wysyłka (do ok. 900 KB) nie trwa dłużej niż to — zawieszone połączenie nie może blokować kolejki. */
export const SEND_TIMEOUT_MS = 25_000;

/** Profil, do którego dopisujemy nagranie (z `Accounts.profile()`; null = tryb lokalny, bez kont). */
export interface RecordingProfile {
  id: string;
  recordVoice?: boolean;
}

export interface RecordingSubmit {
  phase: string;
  attempt: AttemptRecord;
  pcm: Int16Array;
  /** Surowe odpowiedzi Azure tej próby (z `AssessHooks.onAzureRaw`); brak = nie było czego zapisać. */
  azure?: AzureRawPair;
  /** Profil aktywny, gdy dziecko zaczęło mówić (z `RecordingSink.current()`); jeśli do chwili wyniku się zmienił, nagranie jest pomijane. */
  profileId?: string | null;
}

export interface RecordingSink {
  /** Dopisuje nagranie do kolejki (o ile zgoda jest włączona) i uruchamia wysyłkę w tle. Nigdy nie rzuca wyjątku. */
  submit(r: RecordingSubmit): void;
  /** Id aktywnego profilu w tej chwili (null = tryb lokalny albo nikt nie jest zalogowany). */
  current(): string | null;
  /** Ile nagrań czeka na wysłanie. */
  pending(): number;
  /** Próbuje wysłać to, co czeka; spełnia się, gdy kolejka jest pusta albo została do ponowienia. */
  flush(): Promise<void>;
}

export interface RecordingDeps {
  profile: () => RecordingProfile | null;
  /** Nagłówki uwierzytelniające urządzenia (`Authorization: Bearer nk_…`) — wołane przy każdej wysyłce. */
  headers: () => Record<string, string>;
  fetchImpl?: typeof fetch;
  base?: string;
  /** Planuje ponowienie (domyślnie setTimeout); testy podają własny zegar. */
  later?: (fn: () => void, ms: number) => void;
  /** Limit czasu jednej wysyłki w ms (domyślnie `SEND_TIMEOUT_MS`). */
  timeoutMs?: number;
  /** Kontekst zapisywany przy nagraniu (domyślnie `recordingContext`); testy podają własny. */
  context?: () => RecordingContext;
  /** Serwer odmówił, bo zgoda została cofnięta albo profilu nie ma (403/404) — aplikacja powinna odświeżyć profil (poza ekranem lekcji). */
  onRevoked?: (profileId: string) => void;
}

interface Item {
  profile: string;
  phase: RecordedPhase;
  attempt: AttemptRecord;
  pcm: Int16Array;
  azure?: AzureRawPair;
  context?: RecordingContext;
  tries: number;
}

/**
 * Ile BAJTÓW (UTF-8) może mieć część `azure` żądania. Serwer odrzuca (miękko: zapisuje nagranie bez oryginału) część większą niż `MAX_AZURE_BYTES` = 240 000 z server/recordings.ts;
 * tu jest mniej, żeby narzut obwoluty nie miał znaczenia, a test pilnuje, że ta stała nigdy nie przekracza serwerowej. Mierzymy bajty, nie znaki — znak spoza ASCII to 2–4 bajty.
 */
export const MAX_AZURE_BYTES = 200_000;
const bytesOf = (s: string): number => new TextEncoder().encode(s).length;

/** Część `azure` żądania: surowe JSON-y i kontekst. Gdy za duże, odpada najpierw zwykłe rozpoznawanie, potem ocena (zapisujemy w `omitted`). Null = nie ma co wysyłać. */
export function azurePart(item: Pick<Item, 'azure' | 'context'>): string | null {
  const context = item.context;
  if (!context) return null;
  const raw = item.azure ?? {};
  const omitted: Array<'scripted' | 'plain'> = [];
  const body = (): string =>
    JSON.stringify({ v: 1, ...(raw.scripted !== undefined && !omitted.includes('scripted') ? { scripted: raw.scripted } : {}), ...(raw.plain !== undefined && !omitted.includes('plain') ? { plain: raw.plain } : {}), ...(omitted.length > 0 ? { omitted } : {}), ...(raw.replays ? { replays: raw.replays } : {}), context });
  let text = body();
  for (const drop of ['plain', 'scripted'] as const) {
    if (bytesOf(text) <= MAX_AZURE_BYTES) break;
    if (raw[drop] === undefined) continue;
    omitted.push(drop);
    text = body();
  }
  return bytesOf(text) <= MAX_AZURE_BYTES ? text : null;
}

type Outcome = 'done' | 'drop' | 'retry' | 'revoked';

export function createRecordingQueue(deps: RecordingDeps): RecordingSink {
  const fetchImpl = deps.fetchImpl ?? ((...a: Parameters<typeof fetch>) => fetch(...a));
  const base = deps.base ?? './api';
  const later = deps.later ?? ((fn, ms) => void setTimeout(fn, ms));
  const queue: Item[] = [];
  let running: Promise<void> | null = null;
  let retryPlanned = false;
  /** Profile, którym serwer odmówił (403/404): id → obiekt profilu z chwili odmowy. Dopóki aplikacja ma TEN SAM obiekt (nie wczytała profili na nowo), nic z niego nie przyjmujemy. */
  const refused = new Map<string, RecordingProfile>();
  const contextOf = (): { context?: RecordingContext } => {
    try {
      return { context: (deps.context ?? recordingContext)() };
    } catch {
      return {}; // bez kontekstu nagranie jedzie bez części „azure” — lepsze to niż utrata nagrania
    }
  };

  async function send(item: Item): Promise<Outcome> {
    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    const timer = controller ? setTimeout(() => controller.abort(), deps.timeoutMs ?? SEND_TIMEOUT_MS) : undefined;
    try {
      const form = new FormData();
      form.set('meta', JSON.stringify({ profile: item.profile, phase: item.phase, attempt: item.attempt }));
      form.set('audio', new Blob([wavFromPcm16(item.pcm)], { type: 'audio/wav' }), 'nagranie.wav');
      // oryginał z Azure to dodatek: żaden wyjątek przy jego składaniu nie może kosztować nagrania (inaczej trafiłby do `catch` jako błąd sieci i po kilku próbach nagranie by przepadło)
      let azure: string | null = null;
      try {
        azure = azurePart(item);
      } catch {
        azure = null;
      }
      if (azure !== null) form.set('azure', azure);
      // bez content-type: przeglądarka sama dopisze granicę multipart
      const res = await fetchImpl(`${base}/recordings`, { method: 'POST', cache: 'no-store', headers: deps.headers(), body: form, ...(controller ? { signal: controller.signal } : {}) });
      if (res.ok) return 'done';
      // zgoda cofnięta (403) albo profil zarchiwizowany/usunięty (404): kolejka tego profilu traci sens, a profil trzeba odświeżyć
      if (res.status === 403 || res.status === 404) return 'revoked';
      // 503 „recordings-unavailable” to brak bucketa na serwerze — nie minie od ponawiania; każdy inny 5xx i „zbyt wiele żądań naraz” bywają chwilowe
      if (res.status === 503 && (await errorCode(res)) === 'recordings-unavailable') return 'drop';
      return res.status >= 500 || res.status === 408 ? 'retry' : 'drop'; // reszta (limit, zły plik, urządzenie niesparowane) się nie zmieni
    } catch {
      return 'retry'; // brak sieci albo przekroczony limit czasu
    } finally {
      clearTimeout(timer);
    }
  }

  async function drain(): Promise<void> {
    while (queue.length > 0) {
      const item = queue[0] as Item;
      const outcome = await send(item);
      // w trakcie wysyłki kolejka mogła się zmienić (przepełnienie wyrzuca najstarsze), więc pozycję zdejmujemy po tożsamości, nie po miejscu
      const at = queue.indexOf(item);
      if (outcome === 'revoked') {
        for (let i = queue.length - 1; i >= 0; i--) if ((queue[i] as Item).profile === item.profile) queue.splice(i, 1);
        const active = deps.profile();
        if (active?.id === item.profile) refused.set(item.profile, active);
        try {
          deps.onRevoked?.(item.profile);
        } catch {
          /* odświeżenie profilu nie może przerwać kolejki */
        }
        continue;
      }
      if (outcome === 'retry') {
        if (at < 0) continue; // wypadła z kolejki w trakcie — nie ma czego ponawiać
        item.tries++;
        if (item.tries >= MAX_TRIES) {
          queue.splice(at, 1);
          continue;
        }
        if (!retryPlanned) {
          retryPlanned = true;
          later(() => {
            retryPlanned = false;
            void api.flush();
          }, RETRY_MS);
        }
        return; // kolejne pozycje czekają za tą (kolejność nie ma znaczenia, ale jedna awaria zwykle znaczy brak sieci)
      }
      if (at >= 0) queue.splice(at, 1);
    }
  }

  const api: RecordingSink = {
    submit(r) {
      try {
        const profile = deps.profile();
        if (!profile || profile.recordVoice !== true) return;
        const stale = refused.get(profile.id);
        if (stale !== undefined) {
          if (stale === profile) return; // serwer już odmówił, a profil nie został od tego czasu wczytany na nowo
          refused.delete(profile.id); // wczytany na nowo: wiemy już, co rodzic ustawił
        }
        if (r.profileId !== undefined && r.profileId !== profile.id) return; // w trakcie oceny przełączono profil
        if (!isRecordedPhase(r.phase) || r.attempt.src !== 'azure') return;
        const seconds = r.pcm.length / PCM_RATE;
        if (seconds < MIN_SECONDS || seconds > MAX_SECONDS) return;
        queue.push({ profile: profile.id, phase: r.phase, attempt: r.attempt, pcm: r.pcm, ...(r.azure ? { azure: r.azure } : {}), ...contextOf(), tries: 0 });
        while (queue.length > MAX_QUEUE) queue.shift();
        if (!retryPlanned) void api.flush(); // w przerwie przed ponowieniem nowa próba tylko czeka w kolejce
      } catch {
        /* zapis do strojenia nigdy nie przeszkadza w nauce */
      }
    },
    current: () => deps.profile()?.id ?? null,
    pending: () => queue.length,
    flush() {
      running ??= drain().catch(() => undefined).finally(() => {
        running = null;
        // pozycja dopisana w chwili, gdy pętla już skończyła, nie może zostać bez wysyłki (ponowienie po błędzie ma własny zegar)
        if (queue.length > 0 && !retryPlanned) void api.flush();
      });
      return running;
    },
  };
  return api;
}

/** Kod błędu z odpowiedzi serwera ({error: '…'}); pusty, gdy ciała nie ma albo nie jest JSON-em. */
async function errorCode(res: Response): Promise<string> {
  try {
    const body: unknown = await res.json();
    const e = (body as { error?: unknown } | null)?.error;
    return typeof e === 'string' ? e : '';
  } catch {
    return '';
  }
}
